import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import {
    Megaphone, Check, AlertCircle, ChevronDown, ChevronUp,
    AlertTriangle, RefreshCw, Image, Download, Palette, Bookmark, Trash2, BookmarkCheck,
    Expand, X, Loader2, Type as TextIcon, ClipboardList, Users, Sparkles, Copy,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, GlassSelect, GlassBadge, ModuleHeader, EmailChipInput } from '../ui';
import TextOverlayModal from '../shared/TextOverlayModal';
import { CopyBtn, CharCount } from './adCopyShared';
import LeadFormsTab from './LeadFormPreview';
import '../shared/AssetCardGrid.css';
import {
    generateAdCopy, generateAdImage, AdCopyResult, AdCopyRequest,
    saveAdCreative, listSavedAdCreatives, deleteSavedAdCreative, updateSavedAdCreative, regenerateSavedAdCopy, SavedAdCreative,
    OverlayState, CompositeTextResponse,
    suggestAdInterests, InterestSuggestionsResult,
    AD_IMAGE_TYPES,
    AdImageVariant, RatioGeometry, AD_ASPECT_RATIOS, DERIVED_AD_RATIOS,
    getAdImageVariants, downloadAdImageVariantsZip,
} from '../../services/api';
import './AdCopyModule.css';

// Generator suggestions arrive with literal backslash-n (ad_copy_generator.py's
// "1 or 2 short punchy lines" instruction) — normalise to real newlines before
// seeding the overlay editor, or wrap_text renders the characters \ and n.
const seedOverlayText = (v: unknown): string => String(v ?? '').replace(/\\n/g, '\n');

// Default overlay state fields (mirrors TextOverlayModal's DEFAULT_OVERLAY) used
// only to seed the modal's text field with the generator's suggested overlay
// copy on first open — every other field starts at the modal's own defaults.
const seedOverlayState = (text: string): OverlayState => ({
    text,
    size: 'medium',
    text_color: '#FFFFFF',
    glow_enabled: false,
    glow_color: '#000000',
    shadow_enabled: false,
    shadow_color: '#000000',
    position: 4,
    font_family: 'inter',
    italic: false,
    fallback_to_prompt: false,
    logo_shadow_enabled: false,
    logo_shadow_color: '#000000',
    logo_glow_enabled: false,
    logo_glow_color: '#000000',
});

// ---------------------------------------------------------------------------
// Constants (mirror ad_copy_generator.py)
// ---------------------------------------------------------------------------

const PLATFORMS = [
    { id: 'google',   label: 'Google Ads',   badge: '#4285f4' },
    { id: 'meta',     label: 'Meta',         badge: '#1877f2' },
    { id: 'linkedin', label: 'LinkedIn',     badge: '#0a66c2' },
    { id: 'twitter',  label: 'Twitter / X',  badge: '#000000' },
    { id: 'tiktok',   label: 'TikTok',       badge: '#ff0050' },
];

// Platform key mapping — new keys → legacy multichannel keys for backward compat
const PLATFORM_KEY_MAP: Record<string, string> = {
    google: 'google_search',
    meta: 'meta_facebook',
    linkedin: 'linkedin_sponsored',
};

const AD_ANGLES = [
    { id: 'outcome',      label: 'Outcome',      desc: 'Lead with the desired result — paint the after state' },
    { id: 'pain_point',   label: 'Pain Point',   desc: 'Lead with the problem — agitate the pain before offering relief' },
    { id: 'social_proof', label: 'Social Proof', desc: 'Lead with evidence — numbers, testimonials, or recognition' },
    { id: 'curiosity',    label: 'Curiosity',    desc: 'Create an open loop — make them need to know more' },
    { id: 'comparison',   label: 'Comparison',   desc: 'Position against alternatives — us vs. them or vs. status quo' },
    { id: 'urgency',      label: 'Urgency',      desc: 'Create time or quantity pressure — scarcity signals desirability' },
    { id: 'identity',     label: 'Identity',     desc: 'Appeal to who they are or want to be — tribe membership' },
    { id: 'contrarian',   label: 'Contrarian',   desc: 'Challenge a common belief — take the unexpected position' },
];

const AD_FRAMEWORKS = [
    { id: 'pas',               label: 'Problem–Agitate–Solve',  desc: 'Highlight the pain, intensify it, then present your product as the relief → CTA' },
    { id: 'bab',               label: 'Before–After–Bridge',     desc: 'Show the current painful state, paint the desired future, bridge with your product → CTA' },
    { id: 'social_proof_lead', label: 'Social Proof Lead',       desc: 'Open with impressive evidence (stat / testimonial / recognition) → What you do → Why it works → CTA' },
    { id: 'direct',            label: 'Direct Value',            desc: 'Clear value proposition → Key benefit → Proof point → CTA. No fluff, maximum specificity.' },
];

// Hard safety cap on the standalone wizard's post-copy auto-image batch,
// independent of the "Generate images automatically" toggle — bounds cost
// even at 3 platforms x 3 variants (see ad-copy-auto-image-gen.md Known Risk #1).
const AUTO_IMAGE_BATCH_CAP = 9;

// Character limits per platform field
const CHAR_LIMITS: Record<string, Record<string, number>> = {
    google:   { headlines: 30, descriptions: 90 },
    meta:     { primary_text: 125, headline: 40, description: 30 },
    linkedin: { intro_text: 150, headline: 70, description: 100 },
    twitter:  { tweet_text: 280, card_headline: 70 },
    tiktok:   { ad_text: 80 },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getApiError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
    return fallback;
};

// CharCount / CopyBtn moved to ./adCopyShared (imported above) so
// LeadFormPreview.tsx can reuse them without a circular dependency.

const OverLimitBadge: React.FC<{ limit: number }> = ({ limit }) => (
    <span className="ac-over-limit-badge" title={`Exceeds ${limit}-char platform limit — edit or regenerate`}>
        <AlertTriangle size={11} /> Over {limit} chars
    </span>
);

// ---------------------------------------------------------------------------
// Shared creative fields (visual concept + image overlay)
// ---------------------------------------------------------------------------

interface CreativeProps {
    visual_concept?: string;
    image_overlay_text?: string;
    imageUrl?: string;
    imagePrompt?: string;
    imageLoading?: boolean;
    imageError?: string;
    onGenerateImage?: (customPrompt?: string) => void;
    onOpenOverlay?: () => void;
    onSave?: () => void;
    isSaved?: boolean;
    platform: string;
}

const CreativeSection: React.FC<CreativeProps> = ({
    visual_concept, image_overlay_text, imageUrl, imagePrompt,
    imageLoading, imageError, onGenerateImage, onOpenOverlay, onSave, isSaved, platform
}) => {
    const [showConcept, setShowConcept] = useState(false);
    const [editablePrompt, setEditablePrompt] = useState('');
    const [showPromptEditor, setShowPromptEditor] = useState(false);

    useEffect(() => { if (imagePrompt) setEditablePrompt(imagePrompt); }, [imagePrompt]);

    if (!visual_concept && !image_overlay_text) return null;

    return (
        <div className="ac-creative-section">
            {image_overlay_text && (
                <div className="ac-field">
                    <div className="ac-field-label">
                        <Palette size={11} /> Suggested Overlay Text
                        <CopyBtn text={seedOverlayText(image_overlay_text)} />
                    </div>
                    <p className="ac-field-text">{seedOverlayText(image_overlay_text)}</p>
                </div>
            )}

            {visual_concept && (
                <div className="ac-field">
                    <button className="ac-concept-toggle" onClick={() => setShowConcept(v => !v)}>
                        <span>Visual Concept</span>
                        {showConcept ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                    {showConcept && (
                        <p className="ac-concept-text">{visual_concept}</p>
                    )}
                </div>
            )}

            <div className="ac-image-section">
                {!imageUrl && (
                    <GlassButton
                        variant="secondary"
                        onClick={() => onGenerateImage?.(undefined)}
                        disabled={imageLoading || !visual_concept}
                        style={{ fontSize: '0.8rem', padding: '7px 14px' }}
                    >
                        {imageLoading
                            ? <><div className="ac-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Generating…</>
                            : <><Image size={14} /> Generate Base Image</>
                        }
                    </GlassButton>
                )}
                {imageError && <p className="ac-image-error">{imageError}</p>}
                {imageUrl && (
                    <div className="ac-image-result">
                        <img src={imageUrl} alt="Generated ad creative" className="ac-generated-image" />

                        {/* Editable prompt — shown after image is generated */}
                        <div className="ac-prompt-editor">
                            <button className="ac-concept-toggle" onClick={() => setShowPromptEditor(v => !v)}>
                                <span>Edit image prompt</span>
                                {showPromptEditor ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </button>
                            {showPromptEditor && (
                                <>
                                    <textarea
                                        className="ac-prompt-textarea"
                                        value={editablePrompt}
                                        onChange={e => setEditablePrompt(e.target.value)}
                                        rows={5}
                                    />
                                    <GlassButton
                                        variant="primary"
                                        onClick={() => onGenerateImage?.(editablePrompt)}
                                        disabled={imageLoading || !editablePrompt.trim()}
                                        style={{ fontSize: '0.78rem', padding: '6px 14px', marginTop: 6 }}
                                    >
                                        <RefreshCw size={12} /> Regenerate with edited prompt
                                    </GlassButton>
                                </>
                            )}
                        </div>

                        <div className="ac-image-actions">
                            <a href={imageUrl} download={`ad-creative-${platform}.png`} className="ac-download-btn">
                                <Download size={13} /> Download
                            </a>
                            <GlassButton
                                variant="secondary"
                                onClick={onOpenOverlay}
                                disabled={imageLoading}
                                style={{ fontSize: '0.78rem', padding: '5px 10px' }}
                            >
                                <TextIcon size={12} /> Add Overlay
                            </GlassButton>
                            <GlassButton
                                variant="secondary"
                                onClick={() => onGenerateImage?.(editablePrompt || undefined)}
                                disabled={imageLoading}
                                style={{ fontSize: '0.78rem', padding: '5px 10px' }}
                            >
                                <RefreshCw size={12} /> Regenerate
                            </GlassButton>
                            <GlassButton
                                variant={isSaved ? 'secondary' : 'primary'}
                                onClick={onSave}
                                disabled={isSaved}
                                style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                            >
                                {isSaved ? <><BookmarkCheck size={12} /> Saved</> : <><Bookmark size={12} /> Save Ad</>}
                            </GlassButton>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Platform result cards
// ---------------------------------------------------------------------------

interface PlatformCardProps {
    imageUrl?: string;
    imagePrompt?: string;
    imageLoading?: boolean;
    imageError?: string;
    onGenerateImage?: (customPrompt?: string) => void;
    onOpenOverlay?: () => void;
    onSave?: () => void;
    isSaved?: boolean;
}

const GoogleCard: React.FC<{ data: NonNullable<AdCopyResult['google']> } & PlatformCardProps> = ({ data, imageUrl, imagePrompt, imageLoading, imageError, onGenerateImage, onOpenOverlay, onSave, isSaved }) => (
    <div className="ac-platform-card">
        <div className="ac-platform-header">
            <span className="ac-platform-badge" style={{ background: '#4285f4' }}>Google Ads RSA</span>
            <span className="ac-platform-sub">Responsive Search Ad</span>
        </div>
        <div className="ac-field">
            <div className="ac-field-label">
                Headlines ({data.headlines?.length ?? 0} / 15)
                <span className="ac-limit-note">max 30 chars each</span>
                {data.headlines_over_limit && <OverLimitBadge limit={30} />}
            </div>
            <div className="ac-headlines-grid">
                {(data.headlines ?? []).map((h, i) => (
                    <div key={i} className="ac-headline-row">
                        <span className="ac-headline-num">{i + 1}</span>
                        <span className="ac-headline-text">{h}</span>
                        <CharCount text={h} limit={30} />
                        <CopyBtn text={h} />
                    </div>
                ))}
            </div>
        </div>
        <div className="ac-field">
            <div className="ac-field-label">
                Descriptions ({data.descriptions?.length ?? 0} / 4)
                <span className="ac-limit-note">max 90 chars each</span>
            </div>
            {(data.descriptions ?? []).map((d, i) => (
                <div key={i} className="ac-desc-row">
                    <span className="ac-desc-text">{d}</span>
                    <CharCount text={d} limit={90} />
                    <CopyBtn text={d} />
                </div>
            ))}
        </div>
        {data.display_path && (
            <div className="ac-field">
                <div className="ac-field-label">Display Path</div>
                <div className="ac-desc-row">
                    <span className="ac-desc-text" style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{data.display_path}</span>
                    <CopyBtn text={data.display_path} />
                </div>
            </div>
        )}
        <CreativeSection platform="google" visual_concept={(data as any).visual_concept} image_overlay_text={(data as any).image_overlay_text} imageUrl={imageUrl} imagePrompt={imagePrompt} imageLoading={imageLoading} imageError={imageError} onGenerateImage={onGenerateImage} onOpenOverlay={onOpenOverlay} onSave={onSave} isSaved={isSaved} />
    </div>
);

const MetaCard: React.FC<{ data: NonNullable<AdCopyResult['meta']> } & PlatformCardProps> = ({ data, imageUrl, imagePrompt, imageLoading, imageError, onGenerateImage, onOpenOverlay, onSave, isSaved }) => (
    <div className="ac-platform-card">
        <div className="ac-platform-header">
            <span className="ac-platform-badge" style={{ background: '#1877f2' }}>Meta Ads</span>
            <span className="ac-platform-sub">Facebook / Instagram</span>
        </div>
        {[
            { key: 'primary_text', label: 'Primary Text', limit: 125, value: data.primary_text, over: (data as any).primary_text_over_limit },
            { key: 'headline',     label: 'Headline',     limit: 40,  value: data.headline,     over: (data as any).headline_over_limit },
            { key: 'description',  label: 'Description',  limit: 30,  value: data.description,  over: (data as any).description_over_limit },
        ].map(f => f.value ? (
            <div key={f.key} className="ac-field">
                <div className="ac-field-label">
                    {f.label} {f.over && <OverLimitBadge limit={f.limit} />}
                    <CharCount text={f.value} limit={f.limit} />
                    <CopyBtn text={f.value} />
                </div>
                <p className="ac-field-text">{f.value}</p>
            </div>
        ) : null)}
        {data.cta_options && data.cta_options.length > 0 && (
            <div className="ac-field">
                <div className="ac-field-label">CTA Options</div>
                <div className="ac-cta-chips">
                    {data.cta_options.map((c, i) => (
                        <span key={i} className="ac-cta-chip">{c}<CopyBtn text={c} /></span>
                    ))}
                </div>
            </div>
        )}
        <CreativeSection platform="meta" visual_concept={(data as any).visual_concept} image_overlay_text={(data as any).image_overlay_text} imageUrl={imageUrl} imagePrompt={imagePrompt} imageLoading={imageLoading} imageError={imageError} onGenerateImage={onGenerateImage} onOpenOverlay={onOpenOverlay} onSave={onSave} isSaved={isSaved} />
    </div>
);

const LinkedInCard: React.FC<{ data: NonNullable<AdCopyResult['linkedin']> } & PlatformCardProps> = ({ data, imageUrl, imagePrompt, imageLoading, imageError, onGenerateImage, onOpenOverlay, onSave, isSaved }) => (
    <div className="ac-platform-card">
        <div className="ac-platform-header">
            <span className="ac-platform-badge" style={{ background: '#0a66c2' }}>LinkedIn</span>
            <span className="ac-platform-sub">Sponsored Content</span>
        </div>
        {[
            { key: 'intro_text',  label: 'Intro Text',  limit: 150, value: data.intro_text,  over: (data as any).intro_text_over_limit },
            { key: 'headline',    label: 'Headline',    limit: 70,  value: data.headline,    over: (data as any).headline_over_limit },
            { key: 'description', label: 'Description', limit: 100, value: data.description, over: (data as any).description_over_limit },
        ].map(f => f.value ? (
            <div key={f.key} className="ac-field">
                <div className="ac-field-label">
                    {f.label} {f.over && <OverLimitBadge limit={f.limit} />}
                    <CharCount text={f.value} limit={f.limit} />
                    <CopyBtn text={f.value} />
                </div>
                <p className="ac-field-text">{f.value}</p>
            </div>
        ) : null)}
        {data.cta && (
            <div className="ac-field">
                <div className="ac-field-label">CTA <CopyBtn text={data.cta} /></div>
                <span className="ac-cta-chip">{data.cta}</span>
            </div>
        )}
        <CreativeSection platform="linkedin" visual_concept={(data as any).visual_concept} image_overlay_text={(data as any).image_overlay_text} imageUrl={imageUrl} imagePrompt={imagePrompt} imageLoading={imageLoading} imageError={imageError} onGenerateImage={onGenerateImage} onOpenOverlay={onOpenOverlay} onSave={onSave} isSaved={isSaved} />
    </div>
);

const TwitterCard: React.FC<{ data: NonNullable<AdCopyResult['twitter']> } & PlatformCardProps> = ({ data, imageUrl, imagePrompt, imageLoading, imageError, onGenerateImage, onOpenOverlay, onSave, isSaved }) => (
    <div className="ac-platform-card">
        <div className="ac-platform-header">
            <span className="ac-platform-badge" style={{ background: '#000' }}>Twitter / X</span>
        </div>
        {data.tweet_text && (
            <div className="ac-field">
                <div className="ac-field-label">Tweet <CharCount text={data.tweet_text} limit={280} /><CopyBtn text={data.tweet_text} /></div>
                <p className="ac-field-text">{data.tweet_text}</p>
            </div>
        )}
        {data.card_headline && (
            <div className="ac-field">
                <div className="ac-field-label">Card Headline <CharCount text={data.card_headline} limit={70} /><CopyBtn text={data.card_headline} /></div>
                <p className="ac-field-text">{data.card_headline}</p>
            </div>
        )}
        <CreativeSection platform="twitter" visual_concept={(data as any).visual_concept} image_overlay_text={(data as any).image_overlay_text} imageUrl={imageUrl} imagePrompt={imagePrompt} imageLoading={imageLoading} imageError={imageError} onGenerateImage={onGenerateImage} onOpenOverlay={onOpenOverlay} onSave={onSave} isSaved={isSaved} />
    </div>
);

const TikTokCard: React.FC<{ data: NonNullable<AdCopyResult['tiktok']> } & PlatformCardProps> = ({ data, imageUrl, imagePrompt, imageLoading, imageError, onGenerateImage, onOpenOverlay, onSave, isSaved }) => (
    <div className="ac-platform-card">
        <div className="ac-platform-header">
            <span className="ac-platform-badge" style={{ background: '#ff0050' }}>TikTok</span>
        </div>
        {data.ad_text && (
            <div className="ac-field">
                <div className="ac-field-label">
                    Ad Text {(data as any).ad_text_over_limit && <OverLimitBadge limit={80} />}
                    <CharCount text={data.ad_text} limit={80} />
                    <CopyBtn text={data.ad_text} />
                </div>
                <p className="ac-field-text">{data.ad_text}</p>
            </div>
        )}
        <CreativeSection platform="tiktok" visual_concept={(data as any).visual_concept} image_overlay_text={(data as any).image_overlay_text} imageUrl={imageUrl} imagePrompt={imagePrompt} imageLoading={imageLoading} imageError={imageError} onGenerateImage={onGenerateImage} onOpenOverlay={onOpenOverlay} onSave={onSave} isSaved={isSaved} />
    </div>
);

// Resolve result data for either new keys or legacy keys
const getPlatformData = (result: AdCopyResult, platform: string) => {
    if (platform === 'google')   return result.google   ?? result.google_search;
    if (platform === 'meta')     return result.meta     ?? result.meta_facebook;
    if (platform === 'linkedin') return result.linkedin ?? result.linkedin_sponsored;
    if (platform === 'twitter')  return result.twitter;
    if (platform === 'tiktok')   return result.tiktok;
    return undefined;
};

// ---------------------------------------------------------------------------
// Variant result display
// ---------------------------------------------------------------------------

interface ImageState {
    loading: boolean;
    url: string | null;           // currently displayed image (base OR composited)
    baseUrl: string | null;       // pristine pre-overlay generated image
    thumbnailUrl: string | null;  // small WebP sibling for the saved-ad grid card
    prompt: string | null;
    error: string | null;
    overlayState: OverlayState | null;
    // The style this specific image was actually generated with (resolved id
    // from the backend response), NOT the live wizard select — the user can
    // change the select after generating, and handleSave must record what the
    // image really is, not what the select currently shows (IMPR-002).
    imageType: string | null;
}

// Auto-generated image state for one (variant, platform) pair, tracked at the
// AdCopyModule level (see runAutoImageLoop) and seeded into the active
// variant's VariantResult via the autoImages prop below.
interface AutoImageEntry {
    loading: boolean;
    url?: string;
    thumbnailUrl?: string | null;
    prompt?: string | null;
    error?: string;
    // Resolved style id the backend actually used (res.image_type) — carried
    // through so handleSave records what the image really is (IMPR-002).
    imageType?: string;
}

const EMPTY_IMAGE_STATE: ImageState = { loading: false, url: null, baseUrl: null, thumbnailUrl: null, prompt: null, error: null, overlayState: null, imageType: null };
// Stable empty fallbacks for a variant index that hasn't touched its lifted
// state yet — avoids handing VariantResult a fresh {} object identity every
// render (AdCopyModule.tsx's `variantImageStates[activeVariant] ?? {}` would
// otherwise allocate a new object each render).
const EMPTY_VARIANT_IMAGE_STATES: Record<string, ImageState> = {};
const EMPTY_VARIANT_SAVED_IDS: Record<string, string> = {};

const VariantResult: React.FC<{
    result: AdCopyResult;
    platforms: string[];
    brandGuideId: string;
    angle: string;
    framework: string;
    projectId?: string;
    targetAudience: string;
    productService: string;
    goal: string;
    imageType: string;
    // Auto-generated (or in-flight) image state for THIS variant, keyed by
    // platform — undefined for a platform whose auto-image job never ran
    // (auto-generate off, no visual_concept, or past the batch cap).
    autoImages?: Record<string, AutoImageEntry | undefined>;
    // Per-variant state, LIFTED to the parent AdCopyModule and keyed by
    // variant index there (BLOCKER-002, review iteration 1). VariantResult is
    // no longer remounted on tab switch (no `key={activeVariant}` at the call
    // site) — instead each variant's manual images, saved-ad ids and overlay
    // target live in the parent so switching tabs never discards them. This
    // also means the seeding effect's existing "don't clobber a touched
    // platform" guard below is now purely a same-variant safety net, not the
    // only thing standing between one variant's auto-image and another's —
    // state is naturally scoped per-variant instead of shared-and-guarded.
    imageStates: Record<string, ImageState>;
    setImageStates: (updater: (prev: Record<string, ImageState>) => Record<string, ImageState>) => void;
    savedIds: Record<string, string>;
    setSavedIds: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
    overlayTarget: { platform: string } | null;
    setOverlayTarget: (target: { platform: string } | null) => void;
}> = ({ result, platforms, brandGuideId, angle, framework, projectId, targetAudience,
        productService, goal, imageType, autoImages,
        imageStates, setImageStates, savedIds, setSavedIds, overlayTarget, setOverlayTarget }) => {
    const savedPlatforms = new Set(Object.keys(savedIds));

    // Seed imageStates from the auto-image loop's per-platform result as it
    // arrives (loading -> loaded/error), without ever clobbering a platform
    // the user has already interacted with (a manual regenerate already in
    // flight, already loaded, or already errored).
    useEffect(() => {
        if (!autoImages) return;
        setImageStates(prev => {
            let changed = false;
            const next = { ...prev };
            for (const [platform, entry] of Object.entries(autoImages)) {
                if (!entry) continue;
                const existing = prev[platform];
                if (existing && (existing.loading || existing.url || existing.error)) continue;
                changed = true;
                if (entry.loading) {
                    next[platform] = { ...EMPTY_IMAGE_STATE, loading: true };
                } else if (entry.url) {
                    next[platform] = {
                        loading: false,
                        url: entry.url,
                        baseUrl: entry.url,
                        thumbnailUrl: entry.thumbnailUrl ?? null,
                        prompt: entry.prompt ?? null,
                        error: null,
                        overlayState: null,
                        imageType: entry.imageType ?? null,
                    };
                } else if (entry.error) {
                    next[platform] = { ...EMPTY_IMAGE_STATE, error: entry.error };
                }
            }
            return changed ? next : prev;
        });
    }, [autoImages, setImageStates]);

    const handleGenerateImage = useCallback(async (platform: string, customPrompt?: string) => {
        const data = getPlatformData(result, platform) as any;
        if (!data?.visual_concept && !customPrompt) return;
        setImageStates(prev => ({ ...prev, [platform]: { ...EMPTY_IMAGE_STATE, loading: true } }));
        try {
            const res = await generateAdImage({
                visual_concept: customPrompt || data.visual_concept,
                ad_creative: true,
                image_type: imageType,
                platform,
                brand_guide_id: brandGuideId || undefined,
            });
            // Fresh generation — clear any overlay from a previous base image, it
            // no longer applies (Known Risk #2: regenerating orphans the overlay).
            setImageStates(prev => ({
                ...prev,
                [platform]: { loading: false, url: res.image_url, baseUrl: res.image_url, thumbnailUrl: res.thumbnail_url ?? null, prompt: res.image_prompt || null, error: null, overlayState: null, imageType: res.image_type ?? imageType }
            }));
        } catch (e: any) {
            const msg = e?.response?.data?.detail || 'Image generation failed.';
            setImageStates(prev => ({ ...prev, [platform]: { ...EMPTY_IMAGE_STATE, error: msg } }));
        }
    }, [result, brandGuideId, imageType, setImageStates]);

    const handleSave = useCallback(async (platform: string) => {
        const data = getPlatformData(result, platform) as any;
        const img = imageStates[platform];
        if (!data) return;
        try {
            const res = await saveAdCreative({
                platform,
                copy_data: data,
                image_url: img?.url || undefined,
                thumbnail_url: img?.thumbnailUrl ?? undefined,
                image_prompt: img?.prompt || undefined,
                overlay_state: img?.overlayState ?? undefined,
                angle,
                framework,
                project_id: projectId || undefined,
                target_audience: targetAudience.trim() || undefined,
                offer: (result._offer || '').trim() || undefined,
                audience_segments: result._audience_segment ? [result._audience_segment] : undefined,
                product_service: productService.trim() || undefined,
                goal: goal.trim() || undefined,
                // Record the style the image was ACTUALLY generated with, not
                // the live wizard select (which the user may have changed
                // since generating) — IMPR-002.
                image_type: img?.url ? (img?.imageType ?? imageType) : undefined,
            });
            // Capture the saved id so an overlay applied AFTER Save Ad has a PATCH
            // target instead of silently discarding on navigation (IMPR-002).
            setSavedIds(prev => ({ ...prev, [platform]: String(res.id) }));
        } catch (e: any) {
            alert('Failed to save ad creative.');
        }
    }, [result, imageStates, angle, framework, projectId, targetAudience, productService, goal, imageType, setSavedIds]);

    if (result._error) {
        return (
            <div className="ac-error">
                <AlertCircle size={14} />
                <span>Generation failed for this variant: {result._error}</span>
            </div>
        );
    }

    const overlayImg = overlayTarget ? (imageStates[overlayTarget.platform] ?? EMPTY_IMAGE_STATE) : null;
    const overlayData = overlayTarget ? (getPlatformData(result, overlayTarget.platform) as any) : null;

    return (
        <div className="ac-variant-platforms">
            {platforms.map(p => {
                const data = getPlatformData(result, p);
                if (!data) return null;
                const img = imageStates[p] ?? EMPTY_IMAGE_STATE;
                const imgProps = {
                    imageUrl: img.url ?? undefined,
                    imagePrompt: img.prompt ?? undefined,
                    imageLoading: img.loading,
                    imageError: img.error ?? undefined,
                    onGenerateImage: (customPrompt?: string) => handleGenerateImage(p, customPrompt),
                    onOpenOverlay: () => setOverlayTarget({ platform: p }),
                    onSave: () => handleSave(p),
                    isSaved: savedPlatforms.has(p),
                };
                if (p === 'google')   return <GoogleCard   key={p} data={data as any} {...imgProps} />;
                if (p === 'meta')     return <MetaCard     key={p} data={data as any} {...imgProps} />;
                if (p === 'linkedin') return <LinkedInCard key={p} data={data as any} {...imgProps} />;
                if (p === 'twitter')  return <TwitterCard  key={p} data={data as any} {...imgProps} />;
                if (p === 'tiktok')   return <TikTokCard   key={p} data={data as any} {...imgProps} />;
                return null;
            })}

            {overlayTarget && overlayImg?.url && (
                <TextOverlayModal
                    baseImageUrl={overlayImg.overlayState?.base_image_url || overlayImg.baseUrl || overlayImg.url}
                    initialOverlayState={
                        overlayImg.overlayState ??
                        (overlayData?.image_overlay_text ? seedOverlayState(seedOverlayText(overlayData.image_overlay_text)) : null)
                    }
                    fallbackVisualPrompt={overlayImg.prompt || overlayData?.visual_concept || undefined}
                    projectId={projectId}
                    onClose={() => setOverlayTarget(null)}
                    onApplied={async ({ url, overlay_state, thumbnail_url }: CompositeTextResponse) => {
                        const platform = overlayTarget.platform;
                        // Snapshot for rollback if a post-save PATCH fails below.
                        const previousImg = imageStates[platform] ?? EMPTY_IMAGE_STATE;
                        setImageStates(prev => ({
                            ...prev,
                            [platform]: { ...previousImg, url, thumbnailUrl: thumbnail_url ?? null, overlayState: overlay_state },
                        }));
                        setOverlayTarget(null);
                        // If this platform was already saved, there is no other path that
                        // persists a post-save overlay edit — PATCH the saved row directly
                        // (IMPR-002). If not yet saved, the local update above is enough:
                        // it flows into the saveAdCreative() call once Save Ad is clicked.
                        const savedId = savedIds[platform];
                        if (savedId) {
                            try {
                                await updateSavedAdCreative(Number(savedId), { image_url: url, thumbnail_url: thumbnail_url ?? null, overlay_state });
                            } catch (e: any) {
                                setImageStates(prev => ({ ...prev, [platform]: previousImg }));
                                alert(getApiError(e, 'Failed to save the overlay — please try again.'));
                            }
                        }
                    }}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Saved ad preview modal
// ---------------------------------------------------------------------------

const COPY_FIELD_LABELS: Record<string, string> = {
    primary_text: 'Primary Text', headline: 'Headline', description: 'Description',
    intro_text: 'Intro Text', tweet_text: 'Tweet', card_headline: 'Card Headline',
    ad_text: 'Ad Text', display_path: 'Display Path', cta: 'CTA',
};

// Meta-only helper: given this saved ad's (possibly unsaved) draft
// target_audience/offer/audience_segments, suggest search terms to try in
// Meta Ads Manager's Detailed Targeting "People who match" box. Writes
// NOTHING — no persistence, no staleness to track — the panel always
// regenerates on demand and clears its own result whenever the grounding
// drafts change (see the useEffect below), so it can never show a list
// grounded on text the user has since edited or replaced.
//
// The disclaimer rendered here is NEVER frontend-hardcoded — it travels in
// every successful response body (`result.disclaimer`, sourced from
// meta_interest_suggester.SUGGESTION_DISCLAIMER) precisely so this component
// cannot drift from the backend's honesty framing. Do not hardcode a
// replacement string here even for a loading/placeholder state.
const SuggestedInterestsPanel: React.FC<{
    creativeId: number;
    targetAudience: string;
    offer: string;
    audienceSegments: string[];
    brandGuideId?: string;
}> = ({ creativeId, targetAudience, offer, audienceSegments, brandGuideId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<InterestSuggestionsResult | null>(null);
    const [groundedOn, setGroundedOn] = useState<string | null>(null);

    // Clear-on-change: the moment any grounding draft changes, the stale list
    // (and its disclaimer) is removed from the DOM together, and the button
    // reverts to "Suggest Interests". This is what makes staleness
    // structurally impossible rather than merely tracked.
    useEffect(() => {
        setResult(null);
        setError(null);
    }, [targetAudience, offer, audienceSegments]);

    const canSuggest = targetAudience.trim().length > 0;

    const handleSuggest = useCallback(async () => {
        if (!canSuggest) return;
        setLoading(true);
        setError(null);
        try {
            const res = await suggestAdInterests(creativeId, {
                target_audience: targetAudience.trim(),
                offer: offer.trim() || undefined,
                audience_segments: audienceSegments.length ? audienceSegments : undefined,
                brand_guide_id: brandGuideId,
            });
            setResult(res);
            setGroundedOn(res.grounding.target_audience);
        } catch (e: any) {
            setError(getApiError(e, 'Interest suggestions are unavailable right now.'));
        } finally {
            setLoading(false);
        }
    }, [canSuggest, creativeId, targetAudience, offer, audienceSegments, brandGuideId]);

    const handleCopyAll = useCallback(async () => {
        if (!result) return;
        try {
            await navigator.clipboard.writeText(result.suggestions.map(s => s.term).join('\n'));
        } catch { /* clipboard permission denied — per-term copy buttons still work */ }
    }, [result]);

    const stale = !!(result && groundedOn !== null && groundedOn !== targetAudience.trim());

    return (
        <div className="ac-interest-panel">
            <div className="ac-modal-field-label" style={{ marginBottom: 6 }}>
                <Users size={12} /> Suggested Interests
                <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 4 }}>Meta Detailed Targeting</span>
                <span className="ac-risk-badge"><AlertTriangle size={11} /> Unverified</span>
            </div>

            {!canSuggest && (
                <div className="ac-interest-rationale">Add a Target Audience above to get interest suggestions.</div>
            )}

            {canSuggest && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: result?.suggestions.length ? 8 : 0 }}>
                    <button
                        onClick={handleSuggest}
                        disabled={loading}
                        className="ac-download-btn"
                        style={{ background: 'var(--panel-strong)', cursor: loading ? 'wait' : 'pointer' }}
                    >
                        {loading
                            ? <><Loader2 size={13} className="spin" /> Suggesting…</>
                            : <><Sparkles size={13} /> {result?.suggestions.length ? 'Suggest Again' : 'Suggest Interests'}</>}
                    </button>
                    {!!result?.suggestions.length && (
                        <button
                            onClick={handleCopyAll}
                            className="ac-download-btn"
                            style={{ background: 'var(--panel-strong)', cursor: 'pointer' }}
                        >
                            <Copy size={13} /> Copy All Terms
                        </button>
                    )}
                </div>
            )}

            {error && <div style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{error}</div>}

            {stale && (
                <div className="ac-interest-rationale" style={{ color: 'var(--warning)' }}>
                    Grounded on an earlier Target Audience. Suggest again to refresh.
                </div>
            )}

            {!!result?.suggestions.length && (
                <>
                    <div className="ac-interest-disclaimer">{result.disclaimer}</div>
                    <div className="ac-interest-list" style={{ marginTop: 8 }}>
                        {result.suggestions.map((s, i) => (
                            <div key={i} className="ac-interest-row">
                                <span className="ac-interest-bucket">{s.bucket.replace(/_/g, ' ')}</span>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span className="ac-interest-term">{s.term}</span>
                                        <CopyBtn text={s.term} />
                                        {s.flags.map((f, fi) => (
                                            <span key={fi} className="ac-risk-badge" title={f.detail}>
                                                <AlertTriangle size={11} /> {f.rule.replace(/_/g, ' ')}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="ac-interest-rationale">{s.rationale}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {result && result.suggestions.length === 0 && (
                <div className="ac-interest-rationale">No usable suggestions came back. Try a more specific Target Audience.</div>
            )}
        </div>
    );
};

const SavedAdPreviewModal: React.FC<{
    creative: SavedAdCreative;
    projectId?: string;
    brandGuideId?: string;
    onClose: () => void;
    onUpdated?: (updated: Partial<SavedAdCreative>) => void;
}> = ({ creative, projectId, brandGuideId, onClose, onUpdated }) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const platform = PLATFORMS.find(p => p.id === creative.platform);
    const angle = AD_ANGLES.find(a => a.id === creative.angle);

    // Lead Forms tab (Meta ads only — hidden, not disabled, elsewhere). Both tab
    // bodies are conditionally RENDERED but all draft state lives on this
    // component, so unsaved Ad-tab edits survive a round-trip through the
    // Lead Forms tab. Do NOT extract the Ad tab body into a state-owning child.
    const isMeta = creative.platform === 'meta';
    const [modalTab, setModalTab] = useState<'ad' | 'forms'>('ad');
    const [formsCount, setFormsCount] = useState<number>(creative.lead_forms_count ?? 0);
    // Set when Regenerate Copy replaces the saved copy server-side — the Lead
    // Forms tab's `dirty` interlock only sees UNSAVED edits, so without this
    // flag already-generated forms would silently render against copy that no
    // longer exists (cross-feature review XF-IMPR-001). Cleared when the user
    // regenerates forms. Session-local: SavedAdCreative has no updated_at, so
    // cross-session staleness is not detectable today (spec R6 follow-up).
    const [copyReplaced, setCopyReplaced] = useState(false);

    // Belt-and-braces hygiene alongside the ref-diff fix in LeadFormsTab
    // (BLOCKER-001, review iteration 2): `onUpdated` is itself an inline prop
    // one level up (AdCopyModule's SavedAdPreviewModal render site), so this
    // useCallback cannot make the identity truly stable across every render —
    // the ref-diff bail in LeadFormsTab is the actual loop-breaker. This just
    // avoids handing LeadFormsTab a *needlessly* fresh closure on renders where
    // `onUpdated` itself didn't change.
    const handleFormsCountChange = useCallback((n: number) => {
        setFormsCount(n);
        // Same merge rail as image updates — keeps the grid
        // card's badge fresh without a refetch. Count must be
        // persisted rows, never requested types (fail-open).
        onUpdated?.({ lead_forms_count: n });
    }, [onUpdated]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    // ── Copy-field editing (controlled inputs + explicit Save) ─────────────
    const [draftCopy, setDraftCopy] = useState<Record<string, any>>(creative.copy_data || {});
    const [draftAudience, setDraftAudience] = useState<string>(creative.target_audience || '');
    const [draftOffer, setDraftOffer] = useState<string>(creative.offer || '');
    const [draftSegments, setDraftSegments] = useState<string[]>(creative.audience_segments || []);
    const [dirty, setDirty] = useState(false);
    const [copySaving, setCopySaving] = useState(false);
    const [copyError, setCopyError] = useState<string | null>(null);

    // Regenerate Copy — capture form + request state
    const [draftProduct, setDraftProduct] = useState<string>(creative.product_service || '');
    const [draftGoal, setDraftGoal] = useState<string>(creative.goal || '');
    const [regenLoading, setRegenLoading] = useState(false);
    const [regenError, setRegenError] = useState<string | null>(null);
    const [showRegen, setShowRegen] = useState(false);

    const headlinesList: string[] = Array.isArray(draftCopy.headlines) ? draftCopy.headlines : [];
    // Editable field KEYS grow monotonically: seeded from the ORIGINAL creative.copy_data
    // and unioned with any newly-populated string field a regeneration introduces.
    // Never shrinks — clearing a field to '' must not unmount its textarea (BLOCKER-001
    // of specs/completed/saved-ad-audience-offer-edit.md).
    // visual_concept has no COPY_FIELD_LABELS entry — stays a generation input, not editable here.
    const [textFieldKeys, setTextFieldKeys] = useState<string[]>(() =>
        Object.entries(creative.copy_data || {})
            .filter(([k, v]) => typeof v === 'string' && v && COPY_FIELD_LABELS[k])
            .map(([k]) => k)
    );
    const textFields = textFieldKeys.map(k => [k, draftCopy[k]] as [string, any]);
    const ctaOptions: string[] = Array.isArray(draftCopy.cta_options) ? draftCopy.cta_options : [];

    const updateHeadline = (i: number, value: string) => {
        setDraftCopy(prev => {
            const next = [...(Array.isArray(prev.headlines) ? prev.headlines : [])];
            next[i] = value;
            return { ...prev, headlines: next };
        });
        setDirty(true);
    };
    const updateTextField = (k: string, value: string) => {
        setDraftCopy(prev => ({ ...prev, [k]: value }));
        setDirty(true);
    };
    const updateCta = (i: number, value: string) => {
        setDraftCopy(prev => {
            const next = [...(Array.isArray(prev.cta_options) ? prev.cta_options : [])];
            next[i] = value;
            return { ...prev, cta_options: next };
        });
        setDirty(true);
    };

    const handleSaveCopy = useCallback(async () => {
        setCopySaving(true);
        setCopyError(null);
        try {
            const patch = {
                copy_data: draftCopy,
                target_audience: draftAudience.trim() || null,
                offer: draftOffer.trim() || null,
                audience_segments: draftSegments.length ? draftSegments : null,
            };
            await updateSavedAdCreative(creative.id, patch);
            onUpdated?.(patch);
            setDirty(false);
        } catch (e: any) {
            setCopyError(e?.response?.data?.detail || e?.message || 'Failed to save changes.');
        } finally {
            setCopySaving(false);
        }
    }, [creative.id, draftCopy, draftAudience, draftOffer, draftSegments, onUpdated]);

    const handleCancelCopy = () => {
        setDraftCopy(creative.copy_data || {});
        setDraftAudience(creative.target_audience || '');
        setDraftOffer(creative.offer || '');
        setDraftSegments(creative.audience_segments || []);
        setDirty(false);
        setCopyError(null);
        onClose();
    };

    const handleRegenerateCopy = useCallback(async () => {
        const ps = draftProduct.trim();
        const gl = draftGoal.trim();
        if (!ps || !gl) { setRegenError('Product/service and campaign goal are both required.'); return; }
        if (dirty && !window.confirm(
            'You have unsaved copy edits. Regenerating replaces the copy and discards them. Continue?'
        )) return;
        if (!window.confirm(
            'Regenerate the copy for this ad? This overwrites the saved copy. The image and overlay are kept.'
        )) return;
        setRegenLoading(true);
        setRegenError(null);
        try {
            const res = await regenerateSavedAdCopy(creative.id, {
                product_service: ps,
                goal: gl,
                target_audience: draftAudience.trim() || undefined,
                offer: draftOffer.trim() || undefined,
            });
            const next = res.copy_data || {};
            setDraftCopy(next);
            setTextFieldKeys(prev => {
                const add = Object.entries(next).filter(
                    ([k, v]) => typeof v === 'string' && v && COPY_FIELD_LABELS[k] && !prev.includes(k)
                ).map(([k]) => k);
                return add.length ? [...prev, ...add] : prev;
            });
            setDraftProduct(res.product_service || ps);
            setDraftGoal(res.goal || gl);
            setDirty(false);
            setCopyReplaced(true);
            setShowRegen(false);
            setCopyError(null);
            onUpdated?.({ copy_data: next, product_service: res.product_service, goal: res.goal });
        } catch (e: any) {
            setRegenError(e?.response?.data?.detail || e?.message || 'Copy regeneration failed.');
        } finally {
            setRegenLoading(false);
        }
    }, [creative.id, draftProduct, draftGoal, draftAudience, draftOffer, dirty, onUpdated]);

    // ── In-modal image generation ──────────────────────────────────────────
    // A saved ad lands here without an image only when its auto-image step
    // didn't run for it: the standalone wizard's "Generate images
    // automatically" toggle was off, the batch cap (AUTO_IMAGE_BATCH_CAP) was
    // exceeded, or (campaign path) the row wasn't the campaign's first
    // variant, its campaign's auto-image toggle was off, or the ad-image
    // quota (Phase 0 of ad-copy-auto-image-gen.md) was exhausted mid-batch.
    // The preview modal lets the user fetch one on demand here and persists
    // the result back to the SavedAdCreative row via PATCH so it survives the
    // modal closing.
    const [imageUrl, setImageUrl] = useState<string | undefined>(creative.image_url);
    const [thumbnailUrl, setThumbnailUrl] = useState<string | null | undefined>(creative.thumbnail_url);
    const [imagePrompt, setImagePrompt] = useState<string | undefined>(creative.image_prompt);
    const [overlayState, setOverlayState] = useState<OverlayState | null | undefined>(creative.overlay_state);
    // Phase 0 defect (b) guard: a legacy row has an overlay_state (so an
    // overlay WAS applied) but no base_image_url stamp, meaning image_url
    // already has text baked into it. Opening the editor on such a row would
    // composite new text on top of the old text (double-bake) -- disable
    // Add Overlay for it instead. See resolve_clean_base / Known Risk in
    // specs/completed/ad-image-aspect-variants.md.
    const overlayIsLegacyBaked = Boolean(overlayState && !overlayState.base_image_url);
    const [showOverlayModal, setShowOverlayModal] = useState(false);

    // ── Ad image aspect variants (specs/completed/ad-image-aspect-variants.md) ──
    // Fetched once so the Overlay modal opens with the two derived ratios'
    // existing configs (if any) already seeded, and so the Ad tab can show
    // ratio thumbnails / enable the zip download without a second round-trip.
    const [imageVariants, setImageVariants] = useState<AdImageVariant[]>([]);
    // Server-computed geometry (target_w/target_h honour any
    // AIME_AD_VARIANT_*_W/H env override) — passed through to the modal so
    // its crop math/canvas sizing doesn't rely solely on the hardcoded
    // AD_ASPECT_RATIOS client constant, which would silently diverge from
    // the backend if an override is ever set.
    const [ratioGeometry, setRatioGeometry] = useState<RatioGeometry[] | null>(null);
    useEffect(() => {
        if (!isMeta) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await getAdImageVariants(creative.id);
                if (!cancelled) {
                    setImageVariants(res.variants || []);
                    setRatioGeometry(res.ratios || null);
                }
            } catch {
                // Non-fatal — the Overlay modal simply opens with no seeded
                // variant configs, same as an ad that's never had ratios set up.
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creative.id, isMeta]);

    const [zipDownloading, setZipDownloading] = useState(false);
    const [zipDownloadError, setZipDownloadError] = useState<string | null>(null);
    const handleDownloadAllRatios = useCallback(async () => {
        setZipDownloading(true);
        setZipDownloadError(null);
        try {
            // Constraint, not a preference: apiClient's interceptor is what
            // attaches Authorization — a bare <a href="/api/..."> would 401.
            const blob = await downloadAdImageVariantsZip(creative.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ad-${creative.id}-meta-ratios.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e: any) {
            // Button stays available for a retry; surface why it failed
            // rather than swallowing it silently.
            setZipDownloadError(getApiError(e, 'Download failed — please try again.'));
        } finally {
            setZipDownloading(false);
        }
    }, [creative.id]);
    const [imageLoading, setImageLoading] = useState(false);
    const [imageError, setImageError] = useState<string | null>(null);
    const [editablePrompt, setEditablePrompt] = useState<string>(
        creative.image_prompt || (draftCopy as any).visual_concept || ''
    );
    const [modalImageType, setModalImageType] = useState<string>(creative.image_type ?? 'photo');

    const handleGenerate = useCallback(async () => {
        const concept = (editablePrompt || (draftCopy as any).visual_concept || '').trim();
        if (!concept) {
            setImageError('Provide a visual concept first.');
            return;
        }
        setImageLoading(true);
        setImageError(null);
        try {
            const res = await generateAdImage({
                visual_concept: concept,
                ad_creative: true,
                platform: creative.platform,
                image_type: modalImageType,
            });
            setImageUrl(res.image_url);
            setThumbnailUrl(res.thumbnail_url ?? null);
            setImagePrompt(res.image_prompt || concept);
            // Fresh base image — any prior overlay no longer applies to it (Known
            // Risk #2: regenerating orphans the overlay's base_image_url).
            setOverlayState(null);
            // Persist back to the saved ad so it sticks across reloads. Clear
            // overlay_state in the SAME write so the DB row can't retain a stale
            // overlay pointing at a base image that no longer exists.
            try {
                await updateSavedAdCreative(creative.id, {
                    image_url: res.image_url,
                    thumbnail_url: res.thumbnail_url ?? null,
                    image_prompt: res.image_prompt || concept,
                    overlay_state: null,
                    image_type: res.image_type ?? modalImageType,
                });
                onUpdated?.({ image_url: res.image_url, thumbnail_url: res.thumbnail_url ?? null, image_prompt: res.image_prompt || concept, overlay_state: null, image_type: res.image_type ?? modalImageType });
            } catch (e: any) {
                // Image showed in-modal even if persist failed — log a soft error
                console.error('Failed to persist generated image to saved ad:', e);
            }
        } catch (e: any) {
            setImageError(e?.response?.data?.detail || e?.message || 'Image generation failed.');
        } finally {
            setImageLoading(false);
        }
    }, [editablePrompt, draftCopy, creative.id, creative.platform, onUpdated, modalImageType]);

    const handleOverlayApplied = useCallback(async ({ url, overlay_state, thumbnail_url }: CompositeTextResponse) => {
        // Snapshot pre-apply state for rollback on PATCH failure (mirrors
        // CampaignDetail.tsx ~2032-2052 / MultiChannelModule.tsx ~1146-1180).
        const previousImageUrl = imageUrl;
        const previousThumbnailUrl = thumbnailUrl;
        const previousOverlayState = overlayState;
        setImageUrl(url);
        setThumbnailUrl(thumbnail_url ?? null);
        setOverlayState(overlay_state);
        // Meta ads always drive the modal via aspectVariantsEnabled={isMeta}
        // -> the footer always calls handleGenerateAllRatios there, never
        // plain handleSubmit -- so this callback firing for a Meta ad means
        // the 1:1 step of a multi-step run just landed, with the derived-
        // ratio loop still to come. Closing the modal here would unmount it
        // mid-run: every subsequent setGeneratingStep/setRatioErrors becomes
        // a no-op, so a failed derived-ratio crop would be completely
        // invisible to the user (the exact failure-path visibility the spec
        // requires). handleGenerateAllRatios calls onClose() itself once the
        // whole sequence finishes (or leaves the modal open on a failure).
        if (!isMeta) {
            setShowOverlayModal(false);
        }
        try {
            await updateSavedAdCreative(creative.id, { image_url: url, thumbnail_url: thumbnail_url ?? null, overlay_state });
            onUpdated?.({ image_url: url, thumbnail_url: thumbnail_url ?? null, overlay_state });
        } catch (e: any) {
            // Revert optimistic update — the DB row never got the new overlay, so
            // the UI must not keep showing it as applied.
            setImageUrl(previousImageUrl);
            setThumbnailUrl(previousThumbnailUrl);
            setOverlayState(previousOverlayState);
            alert(e?.response?.data?.detail || e?.message || 'Failed to save the overlay — please try again.');
        }
    }, [creative.id, onUpdated, imageUrl, thumbnailUrl, overlayState, isMeta]);

    return (
        <div className="ac-modal-overlay" ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }}>
            <div className="ac-modal">
                <div className="ac-modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {platform && <span className="ac-platform-badge" style={{ background: platform.badge }}>{platform.label}</span>}
                        {angle && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{angle.label}</span>}
                        {creative.framework && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', opacity: 0.7 }}>{creative.framework.toUpperCase()}</span>}
                    </div>
                    <button className="ac-modal-close" onClick={onClose}><X size={18} /></button>
                </div>

                {isMeta && (
                    <div className="ac-modal-tabs">
                        <button className={`ac-module-tab ${modalTab === 'ad' ? 'active' : ''}`} onClick={() => setModalTab('ad')}>
                            <Megaphone size={14} /> Ad
                        </button>
                        <button className={`ac-module-tab ${modalTab === 'forms' ? 'active' : ''}`} onClick={() => setModalTab('forms')}>
                            <ClipboardList size={14} /> Lead Forms
                            {formsCount > 0 && <GlassBadge variant="info" size="sm">{formsCount}</GlassBadge>}
                        </button>
                    </div>
                )}

                {modalTab === 'ad' && (
                <div
                    className="ac-modal-body"
                    style={!imageUrl ? { flexDirection: 'column' } : undefined}
                >
                    {imageUrl ? (
                        <div className="ac-modal-image-wrap">
                            <img src={thumbnailUrl || imageUrl} alt="Ad creative" className="ac-modal-image" />
                        </div>
                    ) : (
                        // When there's no image, render the Generate controls as a
                        // full-width row above the copy section. Otherwise the
                        // default flex-row layout would squash the textarea into
                        // the 280px image column and the user would have to drag
                        // to expand it (regression noted 2026-05-07).
                        <div
                            className="ac-modal-field"
                            style={{ background: 'var(--panel-strong)', padding: 12, borderRadius: 8, width: '100%' }}
                        >
                            <div className="ac-modal-field-label" style={{ marginBottom: 8 }}>
                                <Image size={12} /> Generate Ad Image
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <GlassSelect
                                    options={AD_IMAGE_TYPES.map(t => ({ value: t.id, label: t.label }))}
                                    value={modalImageType}
                                    onChange={setModalImageType}
                                />
                                <textarea
                                    value={editablePrompt}
                                    onChange={e => setEditablePrompt(e.target.value)}
                                    placeholder="Visual concept (e.g. close-up of a coffee cup on a busy office desk, warm morning light)"
                                    rows={4}
                                    disabled={imageLoading}
                                    style={{
                                        width: '100%', padding: '8px 10px',
                                        background: 'var(--panel)', border: '1px solid var(--stroke)',
                                        borderRadius: 6, color: 'var(--text)', fontSize: '0.85rem',
                                        fontFamily: 'inherit', resize: 'vertical', minHeight: 80,
                                    }}
                                />
                                <button
                                    onClick={handleGenerate}
                                    disabled={imageLoading}
                                    style={{
                                        padding: '8px 14px', borderRadius: 9999, border: 'none',
                                        background: 'var(--primary-500)', color: 'white',
                                        cursor: imageLoading ? 'wait' : 'pointer',
                                        fontSize: '0.85rem', fontWeight: 600,
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        alignSelf: 'flex-start', opacity: imageLoading ? 0.7 : 1,
                                    }}
                                >
                                    {imageLoading
                                        ? <><Loader2 size={14} className="spin" /> Generating…</>
                                        : <><Image size={14} /> Generate Base Image</>}
                                </button>
                                {imageError && (
                                    <div style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{imageError}</div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="ac-modal-copy">
                        {/* Brief fields — target audience / offer / audience segments.
                            Always rendered (never hidden when empty) so a pre-migration
                            row can be populated for the first time (Known Risk R3). */}
                        <div className="ac-modal-field">
                            <div className="ac-modal-field-label">Target Audience</div>
                            <textarea
                                className="ac-prompt-textarea"
                                rows={2}
                                value={draftAudience}
                                onChange={e => { setDraftAudience(e.target.value); setDirty(true); }}
                                placeholder="e.g. busy professionals aged 30-45 looking to save time"
                            />
                        </div>
                        {isMeta && (
                            <SuggestedInterestsPanel
                                creativeId={creative.id}
                                targetAudience={draftAudience}
                                offer={draftOffer}
                                audienceSegments={draftSegments}
                                brandGuideId={brandGuideId}
                            />
                        )}
                        <div className="ac-modal-field">
                            <div className="ac-modal-field-label">Offer</div>
                            <textarea
                                className="ac-prompt-textarea"
                                rows={2}
                                value={draftOffer}
                                onChange={e => { setDraftOffer(e.target.value); setDirty(true); }}
                                placeholder="e.g. 20% off your first visit, Free consultation"
                            />
                        </div>
                        <div className="ac-modal-field">
                            <EmailChipInput
                                label="Audience Segments"
                                value={draftSegments}
                                onChange={next => { setDraftSegments(next); setDirty(true); }}
                                placeholder="e.g. first-time customers — press Enter to add"
                                validator={(s) => s.trim().length > 0}
                                errorMessage="Enter a segment"
                            />
                        </div>

                        {/* Headlines list — controlled inputs */}
                        {headlinesList.length > 0 && (
                            <div className="ac-modal-field">
                                <div className="ac-modal-field-label">Headlines ({headlinesList.length})</div>
                                <div className="ac-headlines-grid">
                                    {headlinesList.map((h, i) => (
                                        <div key={i} className="ac-headline-row">
                                            <span className="ac-headline-num">{i + 1}</span>
                                            <input
                                                className="ac-modal-input"
                                                value={h}
                                                onChange={e => updateHeadline(i, e.target.value)}
                                            />
                                            <CharCount text={h} limit={30} />
                                            <CopyBtn text={h} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* String text fields — controlled textareas */}
                        {textFields.map(([k, v]) => {
                            const limit = CHAR_LIMITS[creative.platform]?.[k];
                            const text = String(v ?? '');
                            return (
                                <div key={k} className="ac-modal-field">
                                    <div className="ac-modal-field-label">
                                        {COPY_FIELD_LABELS[k]}
                                        {typeof limit === 'number' && <CharCount text={text} limit={limit} />}
                                        <CopyBtn text={text} />
                                    </div>
                                    <textarea
                                        className="ac-prompt-textarea"
                                        value={text}
                                        onChange={e => updateTextField(k, e.target.value)}
                                        rows={3}
                                    />
                                </div>
                            );
                        })}

                        {/* CTA chips — controlled inputs, pill-shaped like .ac-cta-chip */}
                        {ctaOptions.length > 0 && (
                            <div className="ac-modal-field">
                                <div className="ac-modal-field-label">CTA Options</div>
                                <div className="ac-cta-chips">
                                    {ctaOptions.map((c, i) => (
                                        <span key={i} className="ac-cta-chip">
                                            <input
                                                className="ac-cta-chip-input"
                                                value={c}
                                                size={Math.max(c.length, 6)}
                                                onChange={e => updateCta(i, e.target.value)}
                                            />
                                            <CopyBtn text={c} />
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Image overlay — prefer the applied overlay's own text once one
                            exists (Known Risk #9: copy_data.image_overlay_text remains the
                            generator's original suggestion and is not kept in sync). */}
                        {(overlayState?.text || draftCopy.image_overlay_text) && (
                            <div className="ac-modal-field">
                                <div className="ac-modal-field-label">
                                    <Palette size={11} /> {overlayState?.text ? 'Image Overlay Text (applied)' : 'Suggested Overlay Text'}
                                </div>
                                <div className="ac-modal-field-text">
                                    <span>{seedOverlayText(overlayState?.text || draftCopy.image_overlay_text)}</span>
                                    <CopyBtn text={seedOverlayText(overlayState?.text || draftCopy.image_overlay_text)} />
                                </div>
                            </div>
                        )}

                        {/* Image prompt */}
                        {imagePrompt && (
                            <div className="ac-modal-field">
                                <div className="ac-modal-field-label">Image Prompt</div>
                                <div className="ac-modal-field-text" style={{ fontStyle: 'italic', fontSize: '0.82rem', opacity: 0.8 }}>
                                    <span>{imagePrompt}</span>
                                    <CopyBtn text={imagePrompt} />
                                </div>
                            </div>
                        )}

                        {showRegen && (
                            <div className="ac-modal-field" style={{ background: 'var(--panel-strong)', padding: 12, borderRadius: 8, width: '100%' }}>
                                <div className="ac-modal-field-label" style={{ marginBottom: 8 }}>
                                    <RefreshCw size={12} /> Regenerate Copy
                                </div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                                    Rewrites the headlines, body copy and CTAs for this ad. The image and overlay are kept. Unsaved copy edits are replaced.
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div>
                                        <div className="ac-modal-field-label">Product / Service *</div>
                                        <textarea
                                            className="ac-prompt-textarea"
                                            rows={2}
                                            value={draftProduct}
                                            onChange={e => setDraftProduct(e.target.value)}
                                            disabled={regenLoading}
                                        />
                                    </div>
                                    <div>
                                        <div className="ac-modal-field-label">Campaign Goal *</div>
                                        <textarea
                                            className="ac-prompt-textarea"
                                            rows={2}
                                            value={draftGoal}
                                            onChange={e => setDraftGoal(e.target.value)}
                                            disabled={regenLoading}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button
                                            onClick={handleRegenerateCopy}
                                            disabled={regenLoading}
                                            className="ac-download-btn"
                                            style={{ background: 'var(--panel-strong)', cursor: regenLoading ? 'wait' : 'pointer' }}
                                        >
                                            {regenLoading
                                                ? <><Loader2 size={13} className="spin" /> Regenerating…</>
                                                : <><RefreshCw size={13} /> Regenerate Copy</>}
                                        </button>
                                        <button
                                            onClick={() => { setShowRegen(false); setRegenError(null); }}
                                            disabled={regenLoading}
                                            className="ac-download-btn"
                                            style={{ background: 'var(--panel-strong)', cursor: 'pointer' }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                    {regenError && <div style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{regenError}</div>}
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                            {imageUrl && (
                                <>
                                    <button
                                        onClick={() => setShowOverlayModal(true)}
                                        className="ac-download-btn"
                                        style={{ background: 'var(--panel-strong)', cursor: overlayIsLegacyBaked ? 'not-allowed' : 'pointer', opacity: overlayIsLegacyBaked ? 0.5 : 1 }}
                                        disabled={overlayIsLegacyBaked}
                                        title={overlayIsLegacyBaked
                                            ? "This ad's overlay was applied before AIME tracked the original image — regenerate the image to edit it."
                                            : "Add or edit text overlay"}
                                    >
                                        <TextIcon size={13} /> Add Overlay
                                    </button>
                                    <a href={imageUrl} download={`ad-${creative.platform}-${creative.id}.png`} className="ac-download-btn">
                                        <Download size={13} /> Download Image
                                    </a>
                                    {isMeta && imageVariants.some(v => v.variant_image_url) && (
                                        <button
                                            onClick={handleDownloadAllRatios}
                                            className="ac-download-btn"
                                            style={{ background: 'var(--panel-strong)', cursor: zipDownloading ? 'not-allowed' : 'pointer' }}
                                            disabled={zipDownloading}
                                            title="Download the Square, Vertical and Horizontal files as one zip"
                                        >
                                            <Download size={13} /> {zipDownloading ? 'Preparing…' : 'Download All Ratios (.zip)'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { setImageUrl(undefined); setImagePrompt(undefined); setOverlayState(undefined); setImageVariants([]); setRatioGeometry(null); }}
                                        className="ac-download-btn"
                                        style={{ background: 'var(--panel-strong)', cursor: 'pointer' }}
                                        title="Discard this image and generate a different one"
                                    >
                                        <Image size={13} /> Regenerate Image
                                    </button>
                                </>
                            )}
                            <button
                                onClick={() => {
                                    const next = !showRegen;
                                    if (next) setRegenError(null);
                                    setShowRegen(next);
                                }}
                                className="ac-download-btn"
                                style={{ background: 'var(--panel-strong)', cursor: 'pointer' }}
                                title="Rewrite the copy for this ad, keeping the image"
                            >
                                <RefreshCw size={13} /> Regenerate Copy
                            </button>
                        </div>
                        {zipDownloadError && (
                            <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{zipDownloadError}</div>
                        )}

                        {isMeta && imageVariants.some(v => v.variant_image_url) && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                {DERIVED_AD_RATIOS.map(r => {
                                    const v = imageVariants.find(vv => vv.ratio === r);
                                    if (!v?.variant_image_url) return null;
                                    return (
                                        <img
                                            key={r}
                                            src={v.thumbnail_url || v.variant_image_url}
                                            alt={`${AD_ASPECT_RATIOS[r].ratioLabel} variant`}
                                            title={AD_ASPECT_RATIOS[r].ratioLabel}
                                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--stroke)' }}
                                        />
                                    );
                                })}
                            </div>
                        )}

                        <div className="ac-modal-footer">
                            <GlassButton variant="secondary" onClick={handleCancelCopy}>
                                Cancel
                            </GlassButton>
                            <GlassButton variant="primary" onClick={handleSaveCopy} disabled={!dirty || copySaving}>
                                {copySaving ? <><Loader2 size={13} className="spin" /> Saving…</> : <><Check size={13} /> Save Changes</>}
                            </GlassButton>
                        </div>
                        {copyError && <div style={{ fontSize: '0.78rem', color: 'var(--danger)', marginTop: 6 }}>{copyError}</div>}
                    </div>
                </div>
                )}

                {isMeta && modalTab === 'forms' && (
                    <LeadFormsTab
                        creative={creative}
                        dirty={dirty}
                        projectId={projectId}
                        copyReplaced={copyReplaced}
                        onStaleFormsHandled={() => setCopyReplaced(false)}
                        brandGuideId={brandGuideId}
                        onFormsCountChange={handleFormsCountChange}
                    />
                )}
            </div>

            {showOverlayModal && imageUrl && (
                <TextOverlayModal
                    baseImageUrl={overlayState?.base_image_url || imageUrl}
                    initialOverlayState={
                        overlayState ??
                        (draftCopy.image_overlay_text ? seedOverlayState(seedOverlayText(draftCopy.image_overlay_text)) : null)
                    }
                    fallbackVisualPrompt={imagePrompt || (draftCopy as any).visual_concept || undefined}
                    projectId={projectId}
                    onClose={() => setShowOverlayModal(false)}
                    onApplied={handleOverlayApplied}
                    aspectVariantsEnabled={isMeta}
                    savedAdId={creative.id}
                    initialVariants={imageVariants}
                    ratioGeometry={ratioGeometry}
                    onVariantsSaved={setImageVariants}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main module
// ---------------------------------------------------------------------------

const AdCopyModule: React.FC = () => {
    const ctx = useOutletContext<{ selectedProjectId?: string }>();
    const location = useLocation();

    const [productService, setProductService] = useState('');
    const [targetAudience, setTargetAudience] = useState('');
    const [goal, setGoal] = useState('');
    const [offer, setOffer] = useState('');
    const [offerInferred, setOfferInferred] = useState(false);
    const [audienceSegments, setAudienceSegments] = useState<string[]>([]);
    const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['google', 'meta', 'linkedin']);
    const [angle, setAngle] = useState('outcome');
    const [framework, setFramework] = useState('pas');
    const [variantCount, setVariantCount] = useState(1);
    const [brandGuideId, setBrandGuideId] = useState('');
    const [imageType, setImageType] = useState<string>('photo');
    const [autoGenerateImages, setAutoGenerateImages] = useState<boolean>(true);   // DEFAULT ON
    // Auto-generated image state, keyed by [variantIndex][platform] — seeded
    // into the active variant's VariantResult via the autoImages prop.
    const [autoImages, setAutoImages] = useState<Record<number, Record<string, AutoImageEntry>>>({});
    // Per-variant state LIFTED from VariantResult (BLOCKER-002, review
    // iteration 1) — manual image generations, saved-ad ids and the open
    // overlay target, all keyed by variant index so switching tabs no longer
    // discards them (VariantResult is no longer remounted via `key=`).
    const [variantImageStates, setVariantImageStates] = useState<Record<number, Record<string, ImageState>>>({});
    const [variantSavedIds, setVariantSavedIds] = useState<Record<number, Record<string, string>>>({});
    const [variantOverlayTargets, setVariantOverlayTargets] = useState<Record<number, { platform: string } | null>>({});
    // Declared here (rather than further down with the other simple useState
    // calls) because the stable per-variant setters below close over it.
    const [activeVariant, setActiveVariant] = useState(0);
    // Stable per-variant setters handed down to VariantResult (BLOCKER-004,
    // review iteration 2). Without useCallback these were fresh arrow
    // closures every render; the child's seeding effect depends on
    // `setImageStates`'s identity, so a new closure each render re-fired the
    // effect forever — an unbounded render loop on the default
    // auto-images-ON path. `[activeVariant]` keeps identity stable across
    // renders that don't switch tabs, and each updater ALSO bails out of the
    // outer spread when the inner slot is reference-unchanged, so even a
    // legitimate `activeVariant` change (or any other caller) can't
    // reintroduce a needless re-render from this level.
    const setImageStatesForActiveVariant = useCallback(
        (updater: (prev: Record<string, ImageState>) => Record<string, ImageState>) => {
            setVariantImageStates(prev => {
                const prevSlot = prev[activeVariant] ?? EMPTY_VARIANT_IMAGE_STATES;
                const nextSlot = updater(prevSlot);
                if (nextSlot === prevSlot) return prev;
                return { ...prev, [activeVariant]: nextSlot };
            });
        },
        [activeVariant]
    );
    const setSavedIdsForActiveVariant = useCallback(
        (updater: (prev: Record<string, string>) => Record<string, string>) => {
            setVariantSavedIds(prev => {
                const prevSlot = prev[activeVariant] ?? EMPTY_VARIANT_SAVED_IDS;
                const nextSlot = updater(prevSlot);
                if (nextSlot === prevSlot) return prev;
                return { ...prev, [activeVariant]: nextSlot };
            });
        },
        [activeVariant]
    );
    const setOverlayTargetForActiveVariant = useCallback(
        (target: { platform: string } | null) => {
            setVariantOverlayTargets(prev => {
                if ((prev[activeVariant] ?? null) === target) return prev;
                return { ...prev, [activeVariant]: target };
            });
        },
        [activeVariant]
    );
    // Monotonically increasing run id, bumped at the start of every
    // handleGenerate call. runAutoImageLoop closes over the id it was
    // started with and checks it against runIdRef.current before every
    // state write — a second click while the first batch is still streaming
    // invalidates the first run's writes instead of letting them land on the
    // second run's (different) results (BLOCKER-001, review iteration 1).
    const runIdRef = useRef(0);
    // True while an auto-image batch is in flight — used to keep the
    // Generate/Regenerate buttons disabled for the whole batch, not just the
    // copy-generation phase, so a user can't easily fire a second run while
    // the first is still streaming images.
    const [imagesGenerating, setImagesGenerating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<AdCopyResult[]>([]);
    const [showAngleDetail, setShowAngleDetail] = useState(false);

    const selectedAngle = AD_ANGLES.find(a => a.id === angle);
    const selectedFramework = AD_FRAMEWORKS.find(f => f.id === framework);

    const togglePlatform = (id: string) => {
        setSelectedPlatforms(prev =>
            prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
        );
    };

    // Post-copy auto-image loop — deliberately a frontend orchestration over
    // the existing POST /multichannel/ad-copy/generate-image endpoint rather
    // than a change to POST /multichannel/ad-copy's contract (see the spec's
    // API Contract Verification #6: that endpoint's shapes stay byte-identical).
    // Sequential, not Promise.all — generate_image_for_user is a multi-second
    // Vertex call; N in parallel would hammer the provider, risk the 120s
    // axios ceiling on tail requests, and race Phase 0's quota pre-flight.
    // Fail-open per job (whitepaper_media pattern): one job's error (including
    // a Phase 0 429) never aborts the batch — that card just falls back to
    // its own manual "Generate Base Image" button.
    // `runId` is captured by the CALLER (handleGenerate) at the moment this
    // run starts, not read from the ref here — every write below re-checks
    // runIdRef.current === runId first and discards itself if a newer run
    // has since started (BLOCKER-001). This is what stops run #1's
    // still-streaming images from landing on run #2's (already replaced)
    // `results`/`autoImages` state after a second click.
    const runAutoImageLoop = useCallback(async (generated: AdCopyResult[], runId: number) => {
        if (!autoGenerateImages) return;
        const jobs: { v: number; platform: string; concept: string }[] = [];
        for (let v = 0; v < generated.length; v++) {
            if ((generated[v] as any)?._error) continue;   // skip failed variants
            for (const p of selectedPlatforms) {
                const d = getPlatformData(generated[v], p) as any;
                const concept = (d?.visual_concept || '').trim();
                if (concept) jobs.push({ v, platform: p, concept });
            }
        }
        const capped = jobs.slice(0, AUTO_IMAGE_BATCH_CAP);
        if (capped.length === 0) return;

        setImagesGenerating(true);
        try {
            // Seed a loading entry for every capped job up front so a variant tab
            // the user hasn't opened yet still shows "generating" the moment it is.
            if (runIdRef.current !== runId) return;
            setAutoImages(prev => {
                const next = { ...prev };
                for (const job of capped) {
                    next[job.v] = { ...(next[job.v] || {}), [job.platform]: { loading: true } };
                }
                return next;
            });

            for (const job of capped) {
                let res: Awaited<ReturnType<typeof generateAdImage>>;
                try {
                    res = await generateAdImage({
                        visual_concept: job.concept,
                        ad_creative: true,
                        platform: job.platform,
                        brand_guide_id: brandGuideId || undefined,
                        image_type: imageType,
                    });
                } catch (e: any) {
                    if (runIdRef.current !== runId) continue;   // stale — discard
                    setAutoImages(prev => ({
                        ...prev,
                        [job.v]: {
                            ...(prev[job.v] || {}),
                            [job.platform]: { loading: false, error: getApiError(e, 'Image generation failed.') },
                        },
                    }));
                    continue;
                }
                if (runIdRef.current !== runId) continue;   // a newer run has started — discard
                setAutoImages(prev => ({
                    ...prev,
                    [job.v]: {
                        ...(prev[job.v] || {}),
                        [job.platform]: {
                            loading: false,
                            url: res.image_url,
                            thumbnailUrl: res.thumbnail_url ?? null,
                            prompt: res.image_prompt || null,
                            imageType: res.image_type,
                        },
                    },
                }));
            }
        } finally {
            // Only this run's own flag flip — a newer run already set it back
            // to true for itself, and this stale `finally` must not clear it.
            if (runIdRef.current === runId) setImagesGenerating(false);
        }
    }, [autoGenerateImages, selectedPlatforms, brandGuideId, imageType]);

    const handleGenerate = async () => {
        if (!productService.trim()) { setError('Product/service description is required.'); return; }
        if (!targetAudience.trim()) { setError('Target audience is required.'); return; }
        if (!goal.trim()) { setError('Campaign goal is required.'); return; }
        if (selectedPlatforms.length === 0) { setError('Select at least one platform.'); return; }
        setError(null);
        setLoading(true);
        setResults([]);
        setActiveVariant(0);
        setAutoImages({});
        // New run invalidates whatever the previous run's image loop is still
        // doing — its writes will fail the `runIdRef.current === runId` check
        // in runAutoImageLoop and be silently discarded (BLOCKER-001). Also
        // clear the lifted per-variant state so a fresh generation doesn't
        // show stale saved-ids/manual-images/overlay state from the last run.
        const runId = ++runIdRef.current;
        setVariantImageStates({});
        setVariantSavedIds({});
        setVariantOverlayTargets({});

        try {
            // Generate variant_count variants sequentially (different angles)
            // The backend single-flight call handles one angle at a time
            const angles = AD_ANGLES.map(a => a.id);
            const angleSequence = [angle, ...angles.filter(a => a !== angle)].slice(0, variantCount);

            // Load-bearing: carry the first response's inferred offer/segments forward
            // into later loop iterations via LOCAL variables, not React state — setState
            // is async and would not be visible to the next iteration. Without this, each
            // angle iteration infers its own offer independently (N calls, N different
            // offers/segments), defeating the point of the feature.
            //
            // Segments specifically: each iteration hits the single-variant endpoint
            // (generate_ad_copy_single -> generate_ad_copy_variants(angle_keys=[angle]),
            // so on its own it can only ever infer exactly 1 segment. `variant_count` on
            // the request tells the backend the TRUE total so the FIRST call's inference
            // returns the full N-segment list in `_audience_segments_all`; we then slice
            // one distinct segment per remaining iteration by index instead of re-inferring.
            let effOffer = offer.trim() || undefined;
            let effSegments = audienceSegments.length ? audienceSegments : undefined;

            const generated: AdCopyResult[] = [];
            for (let i = 0; i < angleSequence.length; i++) {
                const ang = angleSequence[i];
                const req: AdCopyRequest = {
                    product_service: productService.trim(),
                    target_audience: targetAudience.trim(),
                    goal: goal.trim(),
                    platforms: selectedPlatforms,
                    brand_guide_id: brandGuideId || undefined,
                    project_id: ctx?.selectedProjectId || undefined,
                    angle: ang,
                    framework,
                    offer: effOffer,
                    audience_segments: effSegments ? [effSegments[i % effSegments.length]] : undefined,
                    variant_count: variantCount,
                };
                try {
                    const data = await generateAdCopy(req);
                    generated.push(data);
                    if (!effOffer && data._offer) effOffer = data._offer;          // reuse for later angles
                    if (!effSegments && data._audience_segments_all && data._audience_segments_all.length) {
                        // Only set once, from the first response's full inferred list —
                        // subsequent iterations already send a one-element slice of it.
                        effSegments = data._audience_segments_all;
                    }
                } catch (e: any) {
                    generated.push({ _error: getApiError(e, 'Generation failed'), _angle: ang, _framework: framework } as any);
                }
            }
            setResults(generated);

            // Reflect what the backend actually inferred back into the editable UI
            // state — "populated by the pre-pass but still editable", not read-only.
            if (generated[0]?._offer && !offer.trim()) {
                setOffer(generated[0]._offer);
                setOfferInferred(!!generated[0]._offer_inferred);
            }
            // The full resolved list (typed or inferred) is now authoritative —
            // supersedes the old seenSegments dedupe-from-responses approach.
            if (!audienceSegments.length && effSegments?.length) {
                setAudienceSegments(effSegments);
            }

            // Fire-and-forget: deliberately NOT awaited here. Copy must render
            // immediately (setLoading(false) below fires right after copy
            // generation, not after images) — images stream in per card as
            // runAutoImageLoop's own state updates (autoImages) arrive. See
            // Known Risk #6 in ad-copy-auto-image-gen.md: holding the results
            // behind the image batch would leave the ".ac-loading" spinner
            // showing "Generating N variants…" for minutes with no progress
            // signal, for a step that already completed.
            void runAutoImageLoop(generated, runId).catch(err => {
                // IMPR-005: the job-building block and the two setAutoImages
                // calls above the per-job try/catch inside runAutoImageLoop
                // are NOT covered by that per-job catch — without this, a
                // throw there would be an unhandled promise rejection.
                console.error('Auto-image generation loop failed:', err);
            });
        } catch (e: any) {
            setError(getApiError(e, 'Ad copy generation failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const [moduleTab, setModuleTab] = useState<'generate' | 'saved'>(() => {
        const params = new URLSearchParams(location.search);
        return params.get('tab') === 'saved' ? 'saved' : 'generate';
    });
    const [savedCreatives, setSavedCreatives] = useState<SavedAdCreative[]>([]);
    const [savedLoading, setSavedLoading] = useState(false);
    const [previewCreative, setPreviewCreative] = useState<SavedAdCreative | null>(null);

    const loadSaved = useCallback(async () => {
        setSavedLoading(true);
        try {
            const data = await listSavedAdCreatives(ctx?.selectedProjectId);
            setSavedCreatives(data);
        } catch {} finally { setSavedLoading(false); }
    }, [ctx?.selectedProjectId]);

    React.useEffect(() => {
        if (moduleTab === 'saved') loadSaved();
    }, [moduleTab, loadSaved]);

    const handleDeleteSaved = async (id: number) => {
        if (!window.confirm('Delete this saved ad?')) return;
        try {
            await deleteSavedAdCreative(id);
            setSavedCreatives(prev => prev.filter(c => c.id !== id));
        } catch { alert('Delete failed.'); }
    };

    // Per-card "Download All Ratios" on the saved-ads grid — separate from
    // SavedAdPreviewModal's own handleDownloadAllRatios (that one downloads
    // the currently-open ad; this downloads whichever card's button was
    // clicked). Keyed by ad id so multiple cards can't block each other.
    const [zipDownloadingIds, setZipDownloadingIds] = useState<Set<number>>(new Set());
    const handleCardDownloadAllRatios = async (id: number) => {
        setZipDownloadingIds(prev => new Set(prev).add(id));
        try {
            const blob = await downloadAdImageVariantsZip(id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ad-${id}-meta-ratios.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e: any) {
            alert(getApiError(e, 'Download failed — please try again.'));
        } finally {
            setZipDownloadingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
        }
    };

    const angleOptions = AD_ANGLES.map(a => ({ value: a.id, label: a.label }));
    const frameworkOptions = AD_FRAMEWORKS.map(f => ({ value: f.id, label: `${f.label} — ${f.desc}` }));
    const variantOptions = [1, 2, 3].map(n => ({ value: String(n), label: `${n} variant${n > 1 ? 's' : ''}` }));

    return (
        <div className="ac-page">
            <ModuleHeader title="Ad Copy" breadcrumb={['Marketing', 'Ad Copy']} />

            {/* Module-level tabs */}
            <div className="ac-module-tabs">
                <button className={`ac-module-tab ${moduleTab === 'generate' ? 'active' : ''}`} onClick={() => setModuleTab('generate')}>
                    <Megaphone size={14} /> Generate
                </button>
                <button className={`ac-module-tab ${moduleTab === 'saved' ? 'active' : ''}`} onClick={() => setModuleTab('saved')}>
                    <Bookmark size={14} /> Saved Ads
                </button>
            </div>

            {moduleTab === 'saved' && (
                <div className="ac-saved-grid">
                    {savedLoading && <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Loading…</p>}
                    {!savedLoading && savedCreatives.length === 0 && (
                        <GlassCard style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-secondary)' }}>
                            <Bookmark size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                            <p>No saved ads yet. Generate ad copy and click Save Ad on any image.</p>
                        </GlassCard>
                    )}
                    {savedCreatives.map(c => {
                        // Card heading: the ad's headline, not the image overlay text — headline is
                        // the field that identifies the creative at a glance across every platform.
                        // Google stores multiple headlines (array); Meta/LinkedIn store a single one;
                        // Twitter has no headline field, so card_headline is the closest analogue.
                        const copyD = c.copy_data || {};
                        const headline = (Array.isArray(copyD.headlines) ? copyD.headlines[0] : undefined)
                            || copyD.headline || copyD.card_headline || '';
                        const primaryText = c.copy_data?.primary_text || c.copy_data?.tweet_text || c.copy_data?.ad_text || '';
                        const platform = PLATFORMS.find(p => p.id === c.platform);
                        return (
                            <GlassCard key={c.id} className="ac-saved-card" variant="hover" onClick={() => setPreviewCreative(c)}>
                                {c.image_url ? (
                                    <div className="ac-saved-thumb-wrap" onClick={() => setPreviewCreative(c)}>
                                        <img src={c.thumbnail_url || c.image_url} alt="Saved ad creative" className="ac-saved-thumb" loading="lazy" decoding="async" />
                                        <div className="ac-saved-thumb-hover"><Expand size={20} /></div>
                                    </div>
                                ) : (
                                    <div className="ac-saved-no-image" onClick={() => setPreviewCreative(c)}>
                                        <Image size={24} style={{ opacity: 0.3 }} />
                                    </div>
                                )}
                                <div className="ac-saved-meta">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        {platform && <span className="ac-platform-badge" style={{ background: platform.badge, fontSize: '0.7rem', padding: '2px 8px' }}>{platform.label}</span>}
                                        {c.angle && <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{AD_ANGLES.find(a => a.id === c.angle)?.label}</span>}
                                        {!!c.lead_forms_count && (
                                            <GlassBadge variant="info" size="sm">
                                                {c.lead_forms_count} form{c.lead_forms_count > 1 ? 's' : ''}
                                            </GlassBadge>
                                        )}
                                    </div>
                                    {headline && <p className="ac-saved-headline">{headline}</p>}
                                    {primaryText && <p className="ac-saved-copy">{primaryText.slice(0, 100)}{primaryText.length > 100 ? '…' : ''}</p>}
                                    {c.audience_segments?.length ? <span className="ac-saved-meta-sub">{c.audience_segments.join(', ')}</span> : null}
                                    <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 8 }}>{new Date(c.created_at).toLocaleDateString()}</p>
                                </div>
                                {/* The whole card opens Preview (GlassCard's onClick above) — stop
                                    propagation here so clicking any action doesn't also open it. */}
                                <div className="ac-saved-actions" onClick={e => e.stopPropagation()}>
                                    {c.image_url && (() => {
                                        // Raw, pre-overlay image — same resolution the backend's
                                        // resolve_clean_base() uses: the base_image_url an overlay
                                        // was applied FROM, when one exists; otherwise image_url is
                                        // already the raw image (no overlay ever applied). This is
                                        // the file still kept on disk that any overlay/ratio work is
                                        // generated from, per the user's request — the card no longer
                                        // downloads the overlay-baked composite.
                                        const rawUrl = c.overlay_state?.base_image_url ?? c.image_url;
                                        return (
                                            <a href={rawUrl} download={`ad-${c.platform}-${c.id}.png`} className="ac-download-btn" style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
                                                <Download size={12} /> Download Image
                                            </a>
                                        );
                                    })()}
                                    {c.platform === 'meta' && !!c.image_variants_count && (
                                        <button
                                            className="ac-download-btn"
                                            style={{ fontSize: '0.75rem', padding: '4px 10px', cursor: zipDownloadingIds.has(c.id) ? 'not-allowed' : 'pointer' }}
                                            disabled={zipDownloadingIds.has(c.id)}
                                            onClick={() => handleCardDownloadAllRatios(c.id)}
                                            title="Download the Square, Vertical and Horizontal overlayed files as one zip"
                                        >
                                            <Download size={12} /> {zipDownloadingIds.has(c.id) ? 'Preparing…' : 'Download All Ratios'}
                                        </button>
                                    )}
                                    <button className="ac-delete-btn" onClick={() => handleDeleteSaved(c.id)} title="Delete">
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </GlassCard>
                        );
                    })}
                </div>
            )}

            {moduleTab === 'saved' && null /* hide generate UI when on saved tab */}
            {moduleTab !== 'saved' && (<>

            {/* Form */}
            <GlassCard className="ac-form-card">
                <div className="ac-form-grid">
                    <div className="ac-form-group">
                        <label>Product / Service *</label>
                        <GlassInput
                            placeholder="e.g. AI marketing platform for small businesses"
                            value={productService}
                            onChange={setProductService}
                        />
                    </div>
                    <div className="ac-form-group">
                        <label>Target Audience *</label>
                        <GlassInput
                            placeholder="e.g. busy professionals aged 30-45 looking to save time"
                            value={targetAudience}
                            onChange={setTargetAudience}
                        />
                    </div>
                </div>

                <div className="ac-form-group">
                    <label>
                        Offer
                        {offerInferred && <GlassBadge variant="info" size="sm">AI-suggested</GlassBadge>}
                    </label>
                    <GlassInput
                        placeholder="e.g. 20% off your first visit, Free consultation"
                        value={offer}
                        onChange={(v: string) => { setOffer(v); setOfferInferred(false); }}
                    />
                </div>

                <div className="ac-form-group">
                    <EmailChipInput
                        label="Audience Segments (optional)"
                        placeholder="e.g. first-time customers — press Enter to add"
                        value={audienceSegments}
                        onChange={setAudienceSegments}
                        validator={(s) => s.trim().length > 0}
                        errorMessage="Not a valid entry"
                        helperText="Leave empty to let AI infer segments from your brief."
                    />
                </div>

                <div className="ac-form-group">
                    <label>Campaign Goal *</label>
                    <GlassInput
                        placeholder="e.g. drive free trial signups, increase demo bookings"
                        value={goal}
                        onChange={setGoal}
                    />
                </div>

                {/* Platforms */}
                <div className="ac-form-group">
                    <label>Platforms *</label>
                    <div className="ac-platform-checkboxes">
                        {PLATFORMS.map(p => (
                            <label key={p.id} className={`ac-platform-check ${selectedPlatforms.includes(p.id) ? 'selected' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={selectedPlatforms.includes(p.id)}
                                    onChange={() => togglePlatform(p.id)}
                                />
                                <span className="ac-platform-dot" style={{ background: p.badge }} />
                                {p.label}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Angle + Framework */}
                <div className="ac-form-grid">
                    <div className="ac-form-group">
                        <label>
                            Persuasion Angle
                            <button className="ac-info-btn" onClick={() => setShowAngleDetail(v => !v)}>
                                {showAngleDetail ? <ChevronUp size={12} /> : <ChevronDown size={12} />} What's this?
                            </button>
                        </label>
                        <GlassSelect
                            options={angleOptions}
                            value={angle}
                            onChange={setAngle}
                        />
                        {showAngleDetail && selectedAngle && (
                            <p className="ac-angle-desc">{selectedAngle.desc}</p>
                        )}
                    </div>
                    <div className="ac-form-group">
                        <label>Copy Framework</label>
                        <GlassSelect
                            options={AD_FRAMEWORKS.map(f => ({ value: f.id, label: f.label }))}
                            value={framework}
                            onChange={setFramework}
                        />
                        {selectedFramework && (
                            <p className="ac-angle-desc">{selectedFramework.desc}</p>
                        )}
                    </div>
                </div>

                {/* Variants */}
                <div className="ac-form-group">
                    <label>Variants</label>
                    <div className="ac-variant-btns">
                        {[1, 2, 3].map(n => (
                            <button
                                key={n}
                                className={`ac-variant-btn ${variantCount === n ? 'active' : ''}`}
                                onClick={() => setVariantCount(n)}
                            >
                                {n}
                            </button>
                        ))}
                        <span className="ac-variant-note">
                            {variantCount > 1 ? `${variantCount} angles generated, each covers all platforms` : 'Single variant with selected angle'}
                        </span>
                    </div>
                </div>

                {/* Image style + auto-generate */}
                <div className="ac-form-group">
                    <label>Ad Image Style</label>
                    <GlassSelect
                        options={AD_IMAGE_TYPES.map(t => ({ value: t.id, label: t.label }))}
                        value={imageType}
                        onChange={setImageType}
                    />
                </div>

                <div className="ac-form-group">
                    <label className={`ac-platform-check ${autoGenerateImages ? 'selected' : ''}`}>
                        <input
                            type="checkbox"
                            checked={autoGenerateImages}
                            onChange={() => setAutoGenerateImages(v => !v)}
                        />
                        Generate images automatically
                    </label>
                    {autoGenerateImages && (
                        <p className="ac-angle-desc">
                            {selectedPlatforms.length * variantCount} image(s) will be generated
                            ({selectedPlatforms.length} platform(s) × {variantCount} variant(s)).
                            {selectedPlatforms.length * variantCount > AUTO_IMAGE_BATCH_CAP && (
                                <> Only the first {AUTO_IMAGE_BATCH_CAP} images will be generated automatically — generate the rest individually from each ad card.</>
                            )}
                        </p>
                    )}
                </div>

                {error && (
                    <div className="ac-error">
                        <AlertCircle size={14} /><span>{error}</span>
                    </div>
                )}

                <div className="ac-form-actions">
                    <GlassButton variant="primary" onClick={handleGenerate} disabled={loading || imagesGenerating}>
                        <Megaphone size={16} />
                        {loading ? 'Generating...' : imagesGenerating ? 'Generating images…' : 'Generate Ad Copy'}
                    </GlassButton>
                </div>

                {loading && (
                    <div className="ac-loading">
                        <div className="ac-spinner" />
                        <span>Generating {variantCount > 1 ? `${variantCount} variants` : 'ad copy'}…</span>
                    </div>
                )}
            </GlassCard>

            {/* Results */}
            {results.length > 0 && !loading && (
                <div className="ac-results">
                    {results.length > 1 && (
                        <div className="ac-variant-tabs">
                            {results.map((r, i) => (
                                <button
                                    key={i}
                                    className={`ac-variant-tab ${activeVariant === i ? 'active' : ''} ${r._error ? 'error' : ''}`}
                                    onClick={() => setActiveVariant(i)}
                                >
                                    {r._error ? <AlertCircle size={12} /> : null}
                                    {AD_ANGLES.find(a => a.id === (r._angle || angle))?.label ?? `Variant ${i + 1}`}
                                    {r._audience_segment ? ` — ${r._audience_segment}` : ''}
                                    {r._error && ' (failed)'}
                                </button>
                            ))}
                            <GlassButton variant="secondary" onClick={handleGenerate} disabled={loading || imagesGenerating} style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: '0.8rem' }}>
                                <RefreshCw size={13} /> Regenerate
                            </GlassButton>
                        </div>
                    )}

                    {results[activeVariant] && (
                        <VariantResult
                            result={results[activeVariant]}
                            platforms={selectedPlatforms}
                            brandGuideId={brandGuideId}
                            angle={angle}
                            framework={framework}
                            projectId={ctx?.selectedProjectId}
                            targetAudience={targetAudience}
                            productService={productService}
                            goal={goal}
                            imageType={imageType}
                            autoImages={autoImages[activeVariant]}
                            imageStates={variantImageStates[activeVariant] ?? EMPTY_VARIANT_IMAGE_STATES}
                            setImageStates={setImageStatesForActiveVariant}
                            savedIds={variantSavedIds[activeVariant] ?? EMPTY_VARIANT_SAVED_IDS}
                            setSavedIds={setSavedIdsForActiveVariant}
                            overlayTarget={variantOverlayTargets[activeVariant] ?? null}
                            setOverlayTarget={setOverlayTargetForActiveVariant}
                        />
                    )}

                    {results.length === 1 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                            <GlassButton variant="secondary" onClick={handleGenerate} disabled={loading || imagesGenerating} style={{ padding: '6px 14px', fontSize: '0.8rem' }}>
                                <RefreshCw size={13} /> Regenerate
                            </GlassButton>
                        </div>
                    )}
                </div>
            )}
            </>)}

            {previewCreative && (
                <SavedAdPreviewModal
                    creative={previewCreative}
                    projectId={ctx?.selectedProjectId}
                    brandGuideId={brandGuideId}
                    onClose={() => setPreviewCreative(null)}
                    onUpdated={(patch) => {
                        // Sync the parent list AND the currently-open modal's
                        // creative prop so the new image_url survives close/
                        // reopen and the card thumbnail updates immediately.
                        setSavedCreatives(prev =>
                            prev.map(c => c.id === previewCreative.id ? { ...c, ...patch } : c)
                        );
                        setPreviewCreative(prev => prev ? { ...prev, ...patch } : prev);
                    }}
                />
            )}
        </div>
    );
};

export default AdCopyModule;
